import { IdentityFieldType, identityFields } from "@protontech/autofill/types";
import { getAutocompletes } from "./extract";
import type { FormClassification } from "./fathom";
import { getCachedSubType, setCachedSubType } from "./flags";
import { matchAddress, matchCity, matchCountry, matchFirstName, matchFullName, matchLastName, matchMiddleName, matchOrganization, matchState, matchTelephone, matchZipCode, type ReTest } from "./re";
import { sanitizeStringWithSpaces } from "./text";

type IdentityFieldPredicate = (field: HTMLInputElement, autocompletes: string[], haystack: string) => boolean;
type IdentityFieldMatchParams = { form: FormClassification; searchField: boolean; type: string | null; visible: boolean };

const guard =
  (options: { autocompletes: string[]; number?: boolean }, predicate: ReTest): IdentityFieldPredicate =>
  (field, autocompletes, haystack): boolean => {
    if (!options.number && field.getAttribute("type") === "number") return false;
    if (options.autocompletes.some((match) => autocompletes.includes(match))) return true;
    return predicate(haystack);
  };

/**
 * Field type mapping with associated regular expressions.
 *
 * The order is **crucial** : more specific field types are listed first
 * to ensure accurate matching. This prevents broader types from
 * overriding more specific ones.
 *
 * IE: 'address-city' could match both `ADDRESS` and `CITY`.
 * By prioritizing `CITY` in the list, we ensure it's correctly identified.
 */
const IDENTITY_MATCHERS: [IdentityFieldType, IdentityFieldPredicate][] = [
  [IdentityFieldType.FIRSTNAME, guard({ autocompletes: ["given-name"] }, matchFirstName)],
  [IdentityFieldType.MIDDLENAME, guard({ autocompletes: ["additional-name"] }, matchMiddleName)],
  [IdentityFieldType.LASTNAME, guard({ autocompletes: ["family-name"] }, matchLastName)],
  [IdentityFieldType.FULLNAME, guard({ autocompletes: ["name"] }, matchFullName)],

  [IdentityFieldType.TELEPHONE, guard({ autocompletes: ["tel", "tel-national", "tel-local"], number: true }, matchTelephone)],
  [IdentityFieldType.ORGANIZATION, guard({ autocompletes: ["organization"] }, matchOrganization)],

  [IdentityFieldType.CITY, guard({ autocompletes: ["address-level2"] }, matchCity)],
  [IdentityFieldType.ZIPCODE, guard({ autocompletes: ["postal-code"], number: true }, matchZipCode)],
  [IdentityFieldType.STATE, guard({ autocompletes: ["address-level1"] }, matchState)],
  [IdentityFieldType.COUNTRY, guard({ autocompletes: ["country-name"] }, matchCountry)],
  [IdentityFieldType.ADDRESS, guard({ autocompletes: ["street-address", "address-line1"] }, matchAddress)],
];

const IDENTITY_ATTRIBUTES = ["autocomplete", "name", "id", "data-bhw" /* groupon.com */];
/* `.type` normalizes unknown type attributes (eg "phone") to "text" */
const IDENTITY_INPUT_TYPES = ["tel", "text", "number"];

export const getCachedIdentitySubType = (el: HTMLElement): IdentityFieldType | undefined => {
  const subType = getCachedSubType(el);
  if (subType && identityFields.has(subType)) return subType as IdentityFieldType;
};

export const getIdentityHaystack = (input: HTMLInputElement): string => {
  const attrs = IDENTITY_ATTRIBUTES.map((attr) => input?.getAttribute(attr) ?? "");
  return sanitizeStringWithSpaces(attrs.join(" "));
};

/** Determines the identity field type based on input attributes */
export const getIdentityFieldType = (input: HTMLInputElement): IdentityFieldType | undefined => {
  const cachedSubType = getCachedIdentitySubType(input);
  if (cachedSubType) return cachedSubType;

  /** If an non-identity sub-type was already resolved
   * then exclude from identity matches. This can be the
   * case for ambiguous identity fields matching CCs */
  if (getCachedSubType(input) !== undefined) return;

  /** Exclude non matching input types */
  if (!IDENTITY_INPUT_TYPES.includes(input.type)) return;

  /** Edge-case: avoid flagging `autocomplete="email tel"` as
   * an identity field. We're likely dealing with an email/username. */
  if (input.getAttribute("autocomplete")?.includes("email")) return;

  const haystack = getIdentityHaystack(input);
  const autocompletes = getAutocompletes(input);

  if (haystack) {
    const identityType = IDENTITY_MATCHERS.find(([, test]) => test(input, autocompletes, haystack))?.[0];
    if (identityType) setCachedSubType(input, identityType);
    return identityType;
  }
};

export const matchIdentityField = (input: HTMLInputElement, { visible }: IdentityFieldMatchParams) => {
  if (!visible) return false;
  if (getCachedIdentitySubType(input)) return true;

  const identityType = getIdentityFieldType(input);
  if (!identityType) return false;

  setCachedSubType(input, identityType);
  return true;
};
